import { Box, Grid, Stack, SxProps, Theme, Typography } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import {
  CarrierVisitDirection,
  OrderResponseDto,
  OrderStatus,
  OrderUpdateResponseDto,
} from '@planning/app/api'
import { useUnitTypeIcon } from '@planning/components/UnitType'
import { emptyIndicator } from '@planning/constants'
import { IssueStatusChip } from '@planning/pages/Issues/Components/atoms/IssueStatusChip'
import { IContainerJourneyItem } from '@planning/pages/Issues/Stores/ContainerJourneyItem'
import { OpsIssue } from '@planning/pages/VesselVisit/Components'
import { useTranslate } from '@tolgee/react'
import { ButtonOption, HighlightedText, OptionsButton, useMinimalsTheme } from '@tom-ui/ui'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { FC, useEffect } from 'react'
import { UpdatesLabelChip } from '../../pages/VesselVisit/Components/atoms/UpdatesLabelChip'
import { HoldsDrawerButton } from '../hold/HoldsDrawerButton'
import { ContainerJourneyStatus } from '../molecules/ContainerJourneyStatusChip'
import { ContainerJourneyStatusChipFFWrapper } from '../molecules/ContainerJourneyStatusChipFFWrapper'
import { OrderItemChips } from './OrderItemChips'

export const OrderListItem: FC<{
  order: OrderResponseDto
  containerJourney?: IContainerJourneyItem
  sx?: SxProps<Theme>
  filter?: string
  options?: ButtonOption[]
  opsIssues?: OpsIssue[]
  showBorderBottom?: boolean
}> = observer(
  ({ sx, order, containerJourney, filter, options, showBorderBottom = true, opsIssues }) => {
    const theme = useMinimalsTheme()
    const { t } = useTranslate()

    const unityTypeIcon = useUnitTypeIcon(order.unitType)

    const { issueItemStore, orderUpdateItemStore, orderItemStore } = usePlanningStore()

    const orderIssuesTotal = computed<number>(() => {
      const orderIssues = issueItemStore.issues.filter(
        i => order.notResolvedIssueIds?.includes(i.id) ?? false,
      )

      return orderIssues.length
    }).get()

    const opsIssuesTotal = computed<number>(() => {
      const issues =
        opsIssues?.filter(
          i => order.id === i.orderId && i.carrierVisitIds!.includes(order.carrierVisitId!),
        ) ?? []

      return issues.length
    }).get()

    const orderUpdates = computed<OrderUpdateResponseDto[]>(() => {
      const orderUpdates = orderUpdateItemStore.unreadOrderUpdates.filter(
        i => order.id === i.orderId,
      )

      return orderUpdates
    }).get()

    useEffect(() => {
      if (order.linkedOrderId) orderItemStore.fetchById(order.linkedOrderId)
    }, [order.linkedOrderId, orderItemStore])

    const getContainerNumber = (order: OrderResponseDto) => {
      let containerNumber = t('toBeAssigned', 'To be assigned')

      if (!!order.containerNumber && order.containerNumber !== '') {
        containerNumber = order.containerNumber
      } else if (order.nonNumericOrderId) {
        containerNumber = t('nonNumeric', 'Non-numeric')
      }

      return containerNumber
    }
    const containerJourneyStatus = computed(() => {
      if (containerJourney?.status) return containerJourney.status

      if (
        order.direction === CarrierVisitDirection.Outbound &&
        order.status === OrderStatus.Fulfilled
      )
        return ContainerJourneyStatus.Departed

      return ContainerJourneyStatus.Expected
    }).get()

    return (
      <Grid
        key={`grp-row-${order.id}`}
        container
        paddingX={1.5}
        sx={sx}
        height={'60px'}
        display={'flex'}
        alignItems={'center'}
        borderBottom={showBorderBottom ? `1px solid ${theme.palette.grey[300]}` : ''}
        data-cy='order-group-list-item'
      >
        <Grid
          textAlign={'start'}
          size={{
            xs: 2,
            xl: 2,
          }}
        >
          <Stack direction={'row'} spacing={1} alignItems={'center'}>
            {unityTypeIcon}
            <Box>
              <Typography component={'div'} variant='body2'>
                <HighlightedText highlight={filter} color={theme.palette.info.main}>
                  {getContainerNumber(order)}
                </HighlightedText>
              </Typography>
              <Typography component={'div'} variant='mini'>
                <HighlightedText highlight={filter} color={theme.palette.info.main}>
                  {`${order.referenceNumber ?? emptyIndicator}`}
                </HighlightedText>
              </Typography>
            </Box>
          </Stack>
        </Grid>
        <Grid
          alignSelf={'center'}
          textAlign={'start'}
          size={{
            xs: 7,
            xl: 7,
          }}
        >
          <OrderItemChips order={order} />
        </Grid>
        <Grid textAlign={'end'} onClick={event => event.stopPropagation()} size={3}>
          <Grid container>
            <Grid
              sx={{ placeContent: 'center' }}
              size={{
                xs: 10,
                xl: 11,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  placeContent: 'flex-end',
                  gap: '4px',
                }}
                data-cy={`order-group-list-item-${getContainerNumber(order)}-status`}
              >
                {(orderIssuesTotal > 0 || opsIssuesTotal > 0) && (
                  <IssueStatusChip status='Unresolved' total={orderIssuesTotal + opsIssuesTotal} />
                )}

                {orderUpdates.length > 0 && (
                  <UpdatesLabelChip useIcon updateCount={orderUpdates.length} />
                )}

                <HoldsDrawerButton order={order} />
                <ContainerJourneyStatusChipFFWrapper
                  journeyStatus={containerJourneyStatus}
                  orderStatus={order.status}
                  direction={containerJourney?.data.operationType}
                  inboundCarrierType={containerJourney?.inboundOrder?.data.carrierVisitType}
                  currentPosition={containerJourney?.data.currentPosition}
                  equipment={containerJourney?.data.equipmentType}
                  yardLocations={containerJourney?.data.yardLocations}
                />
              </Box>
            </Grid>
            <Grid
              size={{
                xs: 2,
                xl: 1,
              }}
            >
              {!!options?.length && <OptionsButton options={options} position='bottom' />}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    )
  },
)
